import { Box, Drawer } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useToast } from "./Toast";
import { ACTION_SCREENS } from "./actionScreens";

export const UI = {
  p: "primary.main",
  pDark: "primary.dark",
  p50: "rgba(135, 78, 246, 0.10)",
  p100: "rgba(135, 78, 246, 0.20)",
  ink: "text.primary",
  ink2: "text.secondary",
  ink3: "text.secondary",
  ink4: "text.disabled",
  line: "divider",
  lineIn: "rgba(128,128,128,0.45)",
  bg2: "action.hover",
  bg3: "action.hover",
  surface: "background.paper",
  red: "error.main",
  redBg: "rgba(198, 40, 40, 0.12)",
  redInk: "error.main",
  amber: "warning.main",
  amberBg: "rgba(178, 106, 0, 0.12)",
  amberLine: "rgba(178, 106, 0, 0.35)",
  amberInk: "warning.main",
  green: "success.main",
  greenBg: "rgba(27, 122, 71, 0.12)",
  greenInk: "success.main",
  blue: "info.main",
  blueBg: "rgba(21, 101, 192, 0.12)",
  blueInk: "info.main",
  mono: 'ui-monospace, Menlo, Consolas, "Courier New", monospace',
};

const inputSx = {
  width: "100%",
  border: "1px solid",
  borderColor: UI.lineIn,
  borderRadius: "4px",
  padding: "13px 12px",
  bgcolor: UI.surface,
  color: UI.ink,
  outline: "none",
  fontFamily: "inherit",
  // 16px on mobile stops iOS Safari from auto-zooming the page on focus.
  fontSize: { xs: 16, sm: 14 },
  /* Box shadow cannot take a palette path, so the focus ring is a literal
     that reads acceptably on both themes. */
  "&:focus": {
    borderColor: UI.p,
    boxShadow: "inset 0 0 0 1px rgba(135, 78, 246, 0.9)",
  },
};

const labelSx = {
  position: "absolute",
  top: "-7px",
  left: "9px",
  bgcolor: UI.surface,
  padding: "0 5px",
  fontSize: 11,
  color: UI.ink3,
  zIndex: 1,
};

const ALERT_TONES = {
  w: { bg: UI.amberBg, line: UI.amber, fg: UI.amberInk },
  d: { bg: UI.redBg, line: UI.red, fg: UI.redInk },
  i: { bg: UI.blueBg, line: UI.blue, fg: UI.blueInk },
  g: { bg: UI.greenBg, line: UI.green, fg: UI.greenInk },
};

export const btnSx = (variant, disabled) => {
  if (variant === "flat") {
    return {
      bgcolor: "transparent",
      color: UI.ink3,
      border: 0,
      borderRadius: "4px",
      padding: "8px 10px",
      fontWeight: 500,
      fontSize: 14,
      fontFamily: "inherit",
      cursor: "pointer",
      "&:hover": { color: UI.ink, bgcolor: UI.bg3 },
    };
  }
  if (variant === "ghost") {
    return {
      bgcolor: UI.surface,
      color: UI.p,
      border: "1px solid",
      borderColor: UI.p100,
      borderRadius: "4px",
      padding: "6px 12px",
      fontWeight: 500,
      fontSize: 13,
      fontFamily: "inherit",
      cursor: "pointer",
      whiteSpace: "nowrap",
      "&:hover": { bgcolor: UI.p50 },
    };
  }
  return {
    bgcolor: disabled ? UI.line : UI.p,
    color: disabled ? UI.ink4 : UI.surface,
    border: 0,
    borderRadius: "4px",
    padding: "8px 16px",
    fontWeight: 500,
    letterSpacing: ".2px",
    fontSize: 14,
    fontFamily: "inherit",
    cursor: disabled ? "not-allowed" : "pointer",
    whiteSpace: "nowrap",
    "&:hover": { bgcolor: disabled ? UI.line : UI.pDark },
  };
};

/* Seed the form state from each field's default value. */
const initialValues = (fields) => {
  const out = {};
  const walk = (list) => {
    list.forEach((f) => {
      if (!f) return;
      if (f.t === "two") {
        walk([f.a, f.b]);
        return;
      }
      if (!f.name) return;
      if (f.t === "cbx") out[f.name] = Boolean(f.on);
      else if (f.t === "chips")
        out[f.name] = f.opts?.[f.on ?? 0]?.value ?? f.opts?.[0]?.value ?? "";
      else if (f.t === "sel") out[f.name] = f.v ?? f.opts?.[0]?.value ?? "";
      else out[f.name] = f.v ?? "";
    });
  };
  walk(fields);
  return out;
};

/**
 * Clinic sign-off (Flow 5) — the clinic rep signs directly on the
 * technician's device. Pointer events cover touch/pen/mouse uniformly, and
 * `touch-action: none` stops the page from scrolling while drawing on a
 * phone or tablet, which is the realistic device here.
 */
const SignaturePad = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#1a1a1a";
    // A cleared field (e.g. reopening the drawer for a new job) shouldn't
    // show the previous signature baked into the canvas.
    if (!value) ctx.clearRect(0, 0, canvas.width, canvas.height);
  }, [value]);

  const pointFromEvent = (canvas, e) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) * canvas.width) / rect.width,
      y: ((e.clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const start = (e) => {
    const canvas = canvasRef.current;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    const { x, y } = pointFromEvent(canvas, e);
    canvas.getContext("2d").beginPath();
    canvas.getContext("2d").moveTo(x, y);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    const { x, y } = pointFromEvent(canvas, e);
    const ctx = canvas.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const end = () => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    onChange("");
  };

  return (
    <Box>
      <Box
        component="canvas"
        ref={canvasRef}
        width={500}
        height={160}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        sx={{
          width: "100%",
          height: 160,
          touchAction: "none",
          bgcolor: "#fff",
          border: "1px solid",
          borderColor: UI.lineIn,
          borderRadius: "4px",
          cursor: "crosshair",
        }}
      />
      <Box
        component="button"
        type="button"
        onClick={clear}
        sx={{ ...btnSx("flat"), marginTop: "6px", padding: "4px 8px", fontSize: 12 }}
      >
        Clear signature
      </Box>
    </Box>
  );
};

const Field = ({ f, values, setValue }) => {
  if (!f) return null;
  const v = f.name !== undefined ? values[f.name] : undefined;
  const set = (nv) => setValue(f.name, nv);
  const hint = f.hint ? (
    <Box sx={{ fontSize: 11, color: UI.ink3, marginTop: "5px" }}>{f.hint}</Box>
  ) : null;

  const label = (
    <Box component="label" sx={labelSx}>
      {f.l}
      {f.req ? (
        <Box component="span" sx={{ color: UI.red }}>
          {" *"}
        </Box>
      ) : null}
    </Box>
  );

  switch (f.t) {
    case "ro":
      return (
        <Box
          sx={{
            bgcolor: UI.bg2,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "4px",
            padding: "11px 13px",
          }}
        >
          <Box sx={{ fontSize: 11, color: UI.ink3, marginBottom: "3px" }}>
            {f.k}
          </Box>
          <Box
            sx={{
              fontSize: 14,
              color: UI.ink,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            {f.v === null || f.v === undefined || f.v === "" ? "—" : f.v}
            {f.href ? (
              <Box
                component="a"
                href={f.href}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                sx={{ fontSize: 12, color: UI.p, textDecoration: "none" }}
              >
                Track
              </Box>
            ) : null}
          </Box>
        </Box>
      );

    case "txt":
    case "date":
    case "number":
      return (
        <Box sx={{ marginBottom: "18px" }}>
          <Box sx={{ position: "relative" }}>
            {label}
            <Box
              component="input"
              type={f.t === "txt" ? "text" : f.t}
              value={v ?? ""}
              onChange={(e) => set(e.target.value)}
              sx={inputSx}
            />
          </Box>
          {hint}
        </Box>
      );

    case "ta":
      return (
        <Box sx={{ marginBottom: "18px" }}>
          <Box sx={{ position: "relative" }}>
            {label}
            <Box
              component="textarea"
              value={v ?? ""}
              onChange={(e) => set(e.target.value)}
              sx={{ ...inputSx, minHeight: 78, resize: "vertical" }}
            />
          </Box>
          {hint}
        </Box>
      );

    case "sel":
      return (
        <Box sx={{ marginBottom: "18px" }}>
          <Box sx={{ position: "relative" }}>
            {label}
            <Box
              component="select"
              value={v ?? ""}
              onChange={(e) => set(e.target.value)}
              sx={inputSx}
            >
              {(f.opts || []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Box>
          </Box>
          {hint}
        </Box>
      );

    case "chips":
      return (
        <Box sx={{ marginBottom: "18px" }}>
          <Box sx={{ fontSize: 11, color: UI.ink3, margin: "0 0 7px" }}>
            {f.l}
          </Box>
          <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {(f.opts || []).map((o) => {
              const on = v === o.value;
              return (
                <Box
                  component="button"
                  type="button"
                  key={o.value}
                  onClick={() => set(o.value)}
                  sx={{
                    border: "1px solid",
                    borderColor: on ? UI.p : UI.p100,
                    color: on ? UI.surface : UI.pDark,
                    bgcolor: on ? UI.p : UI.surface,
                    borderRadius: "4px",
                    padding: "8px 15px",
                    fontSize: 13,
                    fontWeight: 500,
                    fontFamily: "inherit",
                    cursor: "pointer",
                  }}
                >
                  {o.label}
                </Box>
              );
            })}
          </Box>
          {hint}
        </Box>
      );

    case "cbx":
      return (
        <Box
          component="label"
          sx={{
            display: "flex",
            gap: "10px",
            alignItems: "flex-start",
            marginBottom: "12px",
            cursor: f.dis ? "not-allowed" : "pointer",
          }}
        >
          <Box
            component="input"
            type="checkbox"
            disabled={Boolean(f.dis)}
            checked={Boolean(v)}
            onChange={(e) => set(e.target.checked)}
            sx={{ width: 18, height: 18, accentColor: UI.p, margin: "1px 0 0" }}
          />
          <Box sx={{ fontSize: 13, color: f.dis ? UI.ink3 : UI.ink }}>
            {f.l}
            {f.s ? (
              <Box
                sx={{
                  display: "block",
                  color: UI.ink3,
                  fontSize: 11,
                  marginTop: "2px",
                }}
              >
                {f.s}
              </Box>
            ) : null}
          </Box>
        </Box>
      );

    case "alert": {
      const tone = ALERT_TONES[f.k] || ALERT_TONES.i;
      return (
        <Box
          sx={{
            borderRadius: "4px",
            padding: "12px 14px",
            fontSize: 13,
            marginBottom: "18px",
            borderLeft: "3px solid",
            borderLeftColor: tone.line,
            bgcolor: tone.bg,
            color: tone.fg,
          }}
        >
          {f.h}
        </Box>
      );
    }

    case "open":
      return (
        <Box
          sx={{
            bgcolor: UI.amberBg,
            border: "1px solid",
            borderColor: UI.amberLine,
            color: UI.amberInk,
            fontSize: 12,
            padding: "10px 13px",
            borderRadius: "4px",
            marginBottom: "18px",
          }}
        >
          <b style={{ fontWeight: 500 }}>Open decision.</b> {f.h}
        </Box>
      );

    case "head":
      return (
        <Box
          sx={{
            margin: "22px 0 10px",
            fontSize: 13,
            fontWeight: 500,
            color: UI.ink,
          }}
        >
          {f.h}
        </Box>
      );

    case "sum":
      return (
        <Box
          sx={{
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "4px",
            overflow: "hidden",
            marginBottom: "18px",
          }}
        >
          {(f.rows || []).map((r, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                padding: "9px 12px",
                borderBottom: i === f.rows.length - 1 ? 0 : "1px solid",
                borderBottomColor: UI.line,
                fontSize: 13,
                bgcolor: r[2] === "tot" ? UI.bg2 : "transparent",
                fontWeight: r[2] === "tot" ? 500 : 400,
                color: UI.ink,
                "& span:last-child": { fontVariantNumeric: "tabular-nums" },
              }}
            >
              <span>{r[0]}</span>
              <span>{r[1]}</span>
            </Box>
          ))}
        </Box>
      );

    case "link":
      return (
        <Box
          sx={{
            bgcolor: UI.bg2,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "4px",
            padding: "11px 13px",
            marginBottom: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          }}
        >
          <Box>
            <Box sx={{ fontSize: 11, color: UI.ink3, marginBottom: "3px" }}>
              {f.k}
            </Box>
            <Box sx={{ fontSize: 14, color: UI.ink }}>{f.s || "File"}</Box>
          </Box>
          {f.href ? (
            <Box
              component="a"
              href={f.href}
              target="_blank"
              rel="noreferrer"
              sx={{
                color: UI.p,
                fontSize: 13,
                fontWeight: 500,
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              {f.l || "Download"}
            </Box>
          ) : (
            <Box sx={{ fontSize: 13, color: UI.ink4 }}>Not available</Box>
          )}
        </Box>
      );

    case "sig":
      return (
        <Box sx={{ marginBottom: "18px" }}>
          <Box sx={{ fontSize: 11, color: UI.ink3, margin: "0 0 7px" }}>
            {f.l}
            {f.req ? (
              <Box component="span" sx={{ color: UI.red }}>
                {" *"}
              </Box>
            ) : null}
          </Box>
          <SignaturePad value={v} onChange={set} />
          {hint}
        </Box>
      );

    case "up":
      return (
        <Box sx={{ marginBottom: "18px" }}>
          <Box
            component="label"
            sx={{
              display: "block",
              border: "1px dashed",
              borderColor: UI.lineIn,
              borderRadius: "4px",
              padding: "22px",
              textAlign: "center",
              color: UI.ink3,
              fontSize: 13,
              bgcolor: UI.bg2,
              cursor: "pointer",
            }}
          >
            <Box
              component="input"
              type="file"
              multiple={Boolean(f.multiple)}
              accept={f.accept}
              onChange={(e) => set(Array.from(e.target.files || []))}
              sx={{ display: "none" }}
            />
            <Box sx={{ color: UI.p, fontWeight: 500, marginBottom: "3px" }}>
              {Array.isArray(v) && v.length
                ? v.map((file) => file.name).join(", ")
                : f.l}
            </Box>
            {f.s}
          </Box>
        </Box>
      );

    case "two":
      return (
        <Box
          sx={{
            display: "grid",
            gap: "14px",
            marginBottom: "18px",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          }}
        >
          <Box>
            <Field f={f.a} values={values} setValue={setValue} />
          </Box>
          <Box>
            <Field f={f.b} values={values} setValue={setValue} />
          </Box>
        </Box>
      );

    default:
      return null;
  }
};

/**
 * Generic action drawer. Renders whichever screen `screenKey` names from
 * ACTION_SCREENS, mirroring the prototype's SCREENS object one for one.
 */
const ActionDrawer = ({ open, screenKey, item, onClose, onSaved }) => {
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const screen = screenKey ? ACTION_SCREENS[screenKey] : null;
  const view = screen && item ? screen(item) : null;

  useEffect(() => {
    if (!open || !view) return;
    setValues(initialValues(view.body));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, screenKey, item?.orderId]);

  const setValue = (name, value) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const invalid = Boolean(view?.validate && view.validate(values, item));

  const handleSubmit = async () => {
    if (!view) return;
    if (view.validate) {
      const message = view.validate(values, item);
      if (message) {
        toast.error(message);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await view.submit(item, values, token);
      // Flow 7: offlineQueue.queueOrSend returns { queued: true } instead of
      // throwing when there's no signal, so this isn't a failure — it's just
      // not confirmed by the server yet.
      toast.success(
        res?.queued
          ? "Saved on this device — will sync once you're back online"
          : view.success || "Saved",
      );
      onSaved(res?.data ?? res ?? null);
    } catch (error) {
      toast.error(
        error.response?.data?.message || view.failure || "Could not save",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open && Boolean(view)}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "min(560px, 100%)" },
          bgcolor: UI.surface,
          backgroundImage: "none",
        },
      }}
    >
      {view && (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Box
            sx={{
              padding: { xs: "16px 16px 12px", sm: "18px 22px 14px" },
              borderBottom: "1px solid",
              borderBottomColor: UI.line,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    fontSize: 17,
                    fontWeight: 500,
                    margin: "0 0 3px",
                    color: UI.ink,
                    overflowWrap: "anywhere",
                  }}
                >
                  {view.title}
                </Box>
                <Box
                  sx={{
                    fontSize: 12,
                    color: UI.ink3,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {item.id ? (
                    <Box
                      component="span"
                      sx={{
                        fontFamily: UI.mono,
                        fontSize: 11,
                        color: UI.ink4,
                        bgcolor: UI.bg2,
                        border: "1px solid",
                        borderColor: UI.line,
                        padding: "1px 5px",
                        borderRadius: "3px",
                      }}
                    >
                      {item.id}
                    </Box>
                  ) : null}
                  <Box component="span" sx={{ overflowWrap: "anywhere", minWidth: 0 }}>{view.sub}</Box>
                </Box>
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onClose}
                aria-label="Close"
                sx={{
                  bgcolor: "transparent",
                  border: 0,
                  fontSize: 20,
                  color: UI.ink3,
                  lineHeight: 1,
                  padding: "2px 6px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  "&:hover": { bgcolor: UI.bg2, color: UI.ink },
                }}
              >
                ×
              </Box>
            </Box>
          </Box>

          <Box sx={{ padding: { xs: "16px", sm: "20px 22px" }, overflow: "auto", flex: 1 }}>
            {view.body.map((f, i) => (
              <Field key={i} f={f} values={values} setValue={setValue} />
            ))}
          </Box>

          <Box
            sx={{
              padding: { xs: "12px 16px", sm: "14px 22px" },
              borderTop: "1px solid",
              borderTopColor: UI.line,
              display: "flex",
              flexDirection: { xs: "column-reverse", sm: "row" },
              gap: "8px",
              justifyContent: "flex-end",
              bgcolor: UI.bg2,
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={onClose}
              sx={{
                ...btnSx("flat"),
                width: { xs: "100%", sm: "auto" },
                minHeight: { xs: "44px", sm: "auto" },
                whiteSpace: "normal",
              }}
            >
              {view.secondary || "Cancel"}
            </Box>
            <Box
              component="button"
              type="button"
              onClick={handleSubmit}
              disabled={submitting || invalid}
              sx={{
                ...btnSx("solid", submitting || invalid),
                width: { xs: "100%", sm: "auto" },
                minHeight: { xs: "44px", sm: "auto" },
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              {submitting ? "Saving" : view.primary}
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

export default ActionDrawer;
